/**
 * @file Verifies one fetched chunk against its manifest entry: byte length
 * against `s`, then WebCrypto sha256 against `h`. Consumer:
 * `readChunkedAttachmentVersion`, which calls it on every chunk before the
 * chunk is allowed into the assembly — the contract forbids delivering
 * unverified bytes.
 * Invariants: `h` is the digest of the *uncompressed* chunk bytes, so the
 * caller decompresses before verifying; the digest is lowercase hex, matching
 * the chunk's own Storage path segment; a mismatch throws rather than returning
 * a verdict, so a caller cannot forget to check one.
 */
import type { AttachmentChunkEntry } from '#types';
import { ChunkedAttachmentError } from './chunkedAttachmentError';

const toLowercaseHex = (digest: ArrayBuffer): string =>
  Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');

/**
 * Throws a `ChunkedAttachmentError` with reason `integrity` unless `bytes` is
 * exactly the chunk `entry` describes. `index` is the chunk's position in the
 * manifest, carried only so the message names which chunk failed.
 */
export const verifyChunkBytes = async (
  entry: AttachmentChunkEntry,
  bytes: ArrayBuffer,
  index: number,
): Promise<void> => {
  if (bytes.byteLength !== entry.s) {
    throw new ChunkedAttachmentError(
      'integrity',
      `Chunk ${index} of this file is ${bytes.byteLength} bytes, but its manifest entry declares ${entry.s}.`,
    );
  }
  const computed = toLowercaseHex(await crypto.subtle.digest('SHA-256', bytes));
  if (computed !== entry.h) {
    throw new ChunkedAttachmentError(
      'integrity',
      `Chunk ${index} of this file does not match its digest: the manifest names ${entry.h}, the fetched bytes hash to ${computed}.`,
    );
  }
};
